import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors, ArrowLeft, FileText, Gear, CaretLeft, CaretRight,
  Lightning, CheckCircle, XCircle, Minus, UserCircle,
  FilePdf, FileXls, Lock, Spinner, ShieldCheck,
  ClockCounterClockwise, Play, X, FloppyDisk, Database,
} from '@phosphor-icons/react';
import { API, RUBRICAS, STATUS_FECHAMENTO, formatCurrency, MESES } from './constants';

// ==================== CONFIG MODAL ====================
const ConfigModal = ({ onClose, onSaved }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/afo/config`).then(r => r.json()).then(d => { setConfig(d); setLoading(false); });
  }, []);

  const toggle = (key) => {
    setConfig(c => ({
      ...c,
      rubricas_ativas: { ...c.rubricas_ativas, [key]: !c.rubricas_ativas[key] }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    await fetch(`${API}/afo/config`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    setSaving(false);
    onSaved();
  };

  if (loading) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="config-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <Gear size={22} weight="bold" className="text-[#1B4332]" />
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Configuracao do Salao</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Rubricas */}
          <div>
            <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3">Rubricas Ativas</p>
            <div className="space-y-2">
              {RUBRICAS.map(r => (
                <label key={r.key} className="flex items-center justify-between p-3 rounded-xl bg-[#F7F6F4] cursor-pointer hover:bg-[#F0F0EE] transition-colors" data-testid={`toggle-${r.key}`}>
                  <span className="text-sm text-[#18181B]">{r.label}</span>
                  <div className={`w-10 h-6 rounded-full transition-colors relative ${config.rubricas_ativas[r.key] ? 'bg-[#1B4332]' : 'bg-[#D4D4D8]'}`}
                    onClick={() => toggle(r.key)}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${config.rubricas_ativas[r.key] ? 'translate-x-5' : 'translate-x-1'}`} />
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* AO Config */}
          {config.rubricas_ativas.ao && (
            <div>
              <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">AO — Ajuda Operacional (Padrao)</p>
              <div className="flex gap-2">
                <select value={config.ao_padrao?.tipo || 'por_dia_valido'}
                  onChange={(e) => setConfig(c => ({ ...c, ao_padrao: { ...c.ao_padrao, tipo: e.target.value } }))}
                  data-testid="select-ao-tipo"
                  className="flex-1 px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20">
                  <option value="por_dia_valido">Por dia valido</option>
                  <option value="valor_fixo_mensal">Valor fixo mensal</option>
                </select>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#A1A1AA]">R$</span>
                  <input type="text" data-testid="input-ao-valor"
                    value={config.ao_padrao?.tipo === 'valor_fixo_mensal' ? (config.ao_padrao?.valor_mensal || '') : (config.ao_padrao?.valor_dia || '')}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value.replace(',','.')) || 0;
                      const field = config.ao_padrao?.tipo === 'valor_fixo_mensal' ? 'valor_mensal' : 'valor_dia';
                      setConfig(c => ({ ...c, ao_padrao: { ...c.ao_padrao, [field]: v } }));
                    }}
                    placeholder="0" className="w-24 pl-8 pr-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20" />
                </div>
              </div>
            </div>
          )}

          {/* Criterio dia valido */}
          <div>
            <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Criterio de Dia Valido</p>
            <div className="flex gap-2">
              {['produziu', 'disponivel'].map(st => {
                const isOn = (config.criterio_dia_valido || []).includes(st);
                return (
                  <button key={st} type="button" data-testid={`criterio-${st}`}
                    onClick={() => {
                      const cur = config.criterio_dia_valido || [];
                      setConfig(c => ({ ...c, criterio_dia_valido: isOn ? cur.filter(x => x !== st) : [...cur, st] }));
                    }}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-medium transition-all ${isOn ? 'bg-[#1B4332] text-white' : 'border border-[#E4E4E7] text-[#71717A] hover:bg-[#F4F4F5]'}`}>
                    {st === 'produziu' ? 'Produziu' : 'Disponivel'}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#A1A1AA] mt-1.5">
              Apenas dias com esses status no COD contam como dia valido para AO.
            </p>
          </div>

          <button onClick={handleSave} disabled={saving} data-testid="btn-salvar-config"
            className="w-full py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:shadow-lg"
            style={{ backgroundColor: '#1B4332' }}>
            {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
            {saving ? 'Salvando...' : 'Salvar Configuracao'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== REGRAS MODAL ====================
const RegrasModal = ({ profissionais, onClose }) => {
  const [selected, setSelected] = useState(null);
  const [regra, setRegra] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRegra = async (pid) => {
    setSelected(pid);
    const res = await fetch(`${API}/afo/regras/${pid}`);
    const d = await res.json();
    setRegra(d);
  };

  const handleSave = async () => {
    if (!selected || !regra) return;
    setSaving(true);
    await fetch(`${API}/afo/regras/${selected}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(regra),
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="regras-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Regras por Colaborador</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6">
          {/* Profissional selector */}
          <div className="flex flex-wrap gap-2 mb-5">
            {profissionais.map(p => (
              <button key={p.profissional_id || p.id} data-testid={`btn-prof-${p.profissional_nome || p.nome}`}
                onClick={() => loadRegra(p.profissional_id || p.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                  selected === (p.profissional_id || p.id) ? 'bg-[#1B4332] text-white' : 'border border-[#E4E4E7] text-[#71717A] hover:bg-[#F4F4F5]'}`}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold"
                  style={{ backgroundColor: selected === (p.profissional_id || p.id) ? 'rgba(255,255,255,0.3)' : '#E4E4E7' }}>
                  {(p.profissional_nome || p.nome)?.charAt(0)}
                </div>
                {p.profissional_nome || p.nome}
              </button>
            ))}
          </div>

          {regra && (
            <div className="space-y-4">
              {/* Comissao Servicos */}
              <div className="p-3 rounded-xl bg-[#F7F6F4]">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Comissao Servicos</p>
                <select value={regra.comissao_servicos?.tipo || 'desligada'} data-testid="select-comissao-tipo"
                  onChange={(e) => setRegra(r => ({ ...r, comissao_servicos: { ...r.comissao_servicos, tipo: e.target.value } }))}
                  className="w-full px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm bg-white focus:outline-none">
                  <option value="desligada">Desligada</option>
                  <option value="fixa">Fixa (%)</option>
                  <option value="progressiva">Progressiva (faixas)</option>
                </select>
                {regra.comissao_servicos?.tipo === 'fixa' && (
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" data-testid="input-comissao-pct" value={regra.comissao_servicos?.percentual || ''}
                      onChange={(e) => setRegra(r => ({ ...r, comissao_servicos: { ...r.comissao_servicos, percentual: parseFloat(e.target.value) || 0 } }))}
                      placeholder="10" className="w-20 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm text-right bg-white" />
                    <span className="text-sm text-[#71717A]">%</span>
                  </div>
                )}
                {regra.comissao_servicos?.tipo === 'progressiva' && (
                  <div className="mt-2 text-xs text-[#A1A1AA]">
                    <p>Faixas configuradas: {JSON.stringify(regra.comissao_servicos?.faixas || [])}</p>
                    <p className="mt-1">Edite via API para faixas complexas.</p>
                  </div>
                )}
              </div>

              {/* Comissao Produtos */}
              <div className="p-3 rounded-xl bg-[#F7F6F4]">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Comissao Produtos</p>
                <select value={regra.comissao_produtos?.tipo || 'desligada'} data-testid="select-prod-tipo"
                  onChange={(e) => setRegra(r => ({ ...r, comissao_produtos: { ...r.comissao_produtos, tipo: e.target.value } }))}
                  className="w-full px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm bg-white focus:outline-none">
                  <option value="desligada">Desligada</option>
                  <option value="fixa">Fixa (%)</option>
                </select>
                {regra.comissao_produtos?.tipo === 'fixa' && (
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" data-testid="input-prod-pct" value={regra.comissao_produtos?.percentual || ''}
                      onChange={(e) => setRegra(r => ({ ...r, comissao_produtos: { ...r.comissao_produtos, percentual: parseFloat(e.target.value) || 0 } }))}
                      placeholder="5" className="w-20 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm text-right bg-white" />
                    <span className="text-sm text-[#71717A]">%</span>
                  </div>
                )}
              </div>

              {/* AO */}
              <div className="p-3 rounded-xl bg-[#F7F6F4]">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">AO — Ajuda Operacional</p>
                <select value={regra.ao?.tipo || 'herdar_salao'} data-testid="select-ao-colab"
                  onChange={(e) => setRegra(r => ({ ...r, ao: { ...r.ao, tipo: e.target.value } }))}
                  className="w-full px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm bg-white focus:outline-none">
                  <option value="herdar_salao">Herdar do salao</option>
                  <option value="personalizada">Personalizada</option>
                  <option value="desligada">Desligada</option>
                </select>
                {regra.ao?.tipo === 'personalizada' && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-[#71717A]">R$/dia:</span>
                    <input type="number" data-testid="input-ao-dia" value={regra.ao?.valor_dia || ''}
                      onChange={(e) => setRegra(r => ({ ...r, ao: { ...r.ao, valor_dia: parseFloat(e.target.value) || 0 } }))}
                      placeholder="50" className="w-20 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm text-right bg-white" />
                  </div>
                )}
              </div>

              <button onClick={handleSave} disabled={saving} data-testid="btn-salvar-regra"
                className="w-full py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#1B4332' }}>
                {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                {saving ? 'Salvando...' : 'Salvar Regra'}
              </button>
            </div>
          )}

          {!regra && <p className="text-sm text-[#A1A1AA] text-center py-8">Selecione um colaborador para configurar.</p>}
        </div>
      </div>
    </div>
  );
};

// ==================== FINALIZAR MODAL ====================
const FinalizarModal = ({ periodo, onClose, onSuccess }) => {
  const [nome, setNome] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await fetch(`${API}/afo/finalizar`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodo, finalizado_por: nome }),
    });
    if (!res.ok) {
      const err = await res.json();
      alert(err?.detail || 'Erro');
      setSaving(false);
      return;
    }
    onSuccess();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="finalizar-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm">
        <div className="p-6 space-y-4">
          <div className="text-center">
            <ShieldCheck size={36} weight="light" className="mx-auto mb-3" style={{ color: '#1B4332' }} />
            <h3 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Finalizar Fechamento</h3>
            <p className="text-xs text-[#71717A] mt-1">Esta acao trava o fechamento de {periodo}. Nao podera ser desfeita.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <input data-testid="input-finalizado-por" type="text" value={nome}
              onChange={(e) => setNome(e.target.value)} placeholder="Seu nome (responsavel)"
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20" />
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">Cancelar</button>
              <button type="submit" disabled={saving || !nome.trim()} data-testid="btn-confirmar-finalizar"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#1B4332' }}>
                {saving ? <Spinner size={16} className="animate-spin" /> : <Lock size={16} weight="bold" />}
                Finalizar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==================== ALIMENTAR FECHAMENTO MODAL ====================
const AlimentarModal = ({ periodo, profissionais, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Load existing production data
      const res = await fetch(`${API}/afo/producao/${periodo}`);
      const existing = await res.json();
      const existMap = {};
      for (const e of existing) existMap[e.profissional_id] = e;

      // Load profissionais from COD
      const profRes = await fetch(`${API}/cod/profissionais`);
      const profs = await profRes.json();

      const list = profs.filter(p => p.ativo !== false).map(p => ({
        profissional_id: p.id,
        profissional_nome: p.nome,
        categoria: p.categoria || 'profissional',
        total_servicos: existMap[p.id]?.total_servicos || 0,
        total_produtos: existMap[p.id]?.total_produtos || 0,
      }));
      setItems(list);
      setLoaded(true);
    };
    load();
  }, [periodo]);

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: parseFloat(value) || 0 } : item));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/afo/producao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periodo, itens: items }),
      });
      onSaved();
    } catch { /* */ } finally { setSaving(false); }
  };

  const totalServicos = items.reduce((s, i) => s + (i.total_servicos || 0), 0);
  const totalProdutos = items.reduce((s, i) => s + (i.total_produtos || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="alimentar-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <Database size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Alimentar Fechamento</h2>
              <p className="text-[11px] text-[#A1A1AA]">Producao do periodo {periodo} — faturamento por colaborador</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        {!loaded ? (
          <div className="p-10 flex items-center justify-center">
            <Spinner size={24} className="animate-spin text-[#A1A1AA]" />
          </div>
        ) : (
          <div className="p-6">
            <p className="text-xs text-[#71717A] mb-4">
              Informe o faturamento total de servicos e produtos por colaborador no periodo.
              Apos salvar, clique em "Gerar Apuracao" para recalcular o fechamento.
            </p>

            {/* Table header */}
            <div className="grid grid-cols-[1fr_auto_130px_130px] gap-2 mb-2 px-1">
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider">Colaborador</span>
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider w-16 text-center">Cat.</span>
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider text-right">Servicos (R$)</span>
              <span className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider text-right">Produtos (R$)</span>
            </div>

            {/* Rows */}
            <div className="space-y-1.5">
              {items.map((item, idx) => (
                <div key={item.profissional_id}
                  className="grid grid-cols-[1fr_auto_130px_130px] gap-2 items-center p-2 rounded-xl hover:bg-[#F7F6F4] transition-colors"
                  data-testid={`alimentar-row-${item.profissional_nome}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
                      style={{ backgroundColor: '#1B4332' }}>
                      {item.profissional_nome?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{item.profissional_nome}</span>
                  </div>
                  <span className="text-[10px] text-[#A1A1AA] capitalize w-16 text-center">{item.categoria}</span>
                  <input
                    data-testid={`input-servicos-${item.profissional_nome}`}
                    type="number"
                    step="0.01"
                    value={item.total_servicos || ''}
                    onChange={(e) => updateItem(idx, 'total_servicos', e.target.value)}
                    placeholder="0,00"
                    className="px-3 py-2 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm text-right font-medium focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
                    style={{ fontFamily: 'Outfit' }}
                  />
                  <input
                    data-testid={`input-produtos-${item.profissional_nome}`}
                    type="number"
                    step="0.01"
                    value={item.total_produtos || ''}
                    onChange={(e) => updateItem(idx, 'total_produtos', e.target.value)}
                    placeholder="0,00"
                    className="px-3 py-2 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm text-right font-medium focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
                    style={{ fontFamily: 'Outfit' }}
                  />
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="grid grid-cols-[1fr_auto_130px_130px] gap-2 mt-3 pt-3 border-t border-[#E4E4E7] px-1">
              <span className="text-sm font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Total</span>
              <span className="w-16" />
              <span className="text-sm font-bold text-right" style={{ fontFamily: 'Outfit', color: '#1B4332' }} data-testid="total-servicos">
                {formatCurrency(totalServicos)}
              </span>
              <span className="text-sm font-bold text-right" style={{ fontFamily: 'Outfit', color: '#1B4332' }} data-testid="total-produtos">
                {formatCurrency(totalProdutos)}
              </span>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving} data-testid="btn-salvar-producao"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:shadow-lg"
                style={{ backgroundColor: '#1B4332' }}>
                {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                {saving ? 'Salvando...' : 'Salvar Producao'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
export const AfoPage = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [fechamento, setFechamento] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showRegras, setShowRegras] = useState(false);
  const [showFinalizar, setShowFinalizar] = useState(false);
  const [showAlimentar, setShowAlimentar] = useState(false);

  const periodo = `${year}-${String(month).padStart(2, '0')}`;

  const fetchFechamento = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/afo/fechamento/${periodo}`);
      const d = await res.json();
      setFechamento(d);
    } catch { setFechamento(null); } finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => { fetchFechamento(); }, [fetchFechamento]);

  const handleGerar = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/afo/gerar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periodo }),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err?.detail || 'Erro');
        return;
      }
      fetchFechamento();
    } catch { /* */ } finally { setGenerating(false); }
  };

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m);
    setYear(y);
  };

  const apuracoes = fechamento?.apuracoes || [];
  const status = fechamento?.status || 'nao_gerado';
  const stCfg = STATUS_FECHAMENTO[status] || STATUS_FECHAMENTO.nao_gerado;
  const isFinalizado = status === 'finalizado';
  const isRascunho = status === 'rascunho';
  const hasData = apuracoes.length > 0;

  const profissionais = apuracoes.filter(a => a.categoria === 'profissional');
  const auxiliares = apuracoes.filter(a => a.categoria === 'auxiliar');
  const estagiarios = apuracoes.filter(a => a.categoria === 'estagiario');
  const outros = apuracoes.filter(a => !['profissional','auxiliar','estagiario'].includes(a.categoria));

  const handleExport = async (format) => {
    const { downloadWithAuth } = await import('@/lib/apiClient');
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    await downloadWithAuth(
      `${API}/afo/export/${format}/${periodo}`,
      `afo_${periodo}.${ext}`
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="afo-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Gestao</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">AFO</span>
          </p>
        </div>

        {/* Title */}
        <section className="mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <FileText size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">AFO</h1>
                  <span className="text-[10px] font-semibold px-2.5 py-1 rounded-lg uppercase tracking-wide"
                    style={{ backgroundColor: stCfg.bg, color: stCfg.color }}>
                    {stCfg.label}
                  </span>
                </div>
                <p className="salonup-text-sm mt-1">Agente de Fechamento Operacional</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="btn-config" onClick={() => setShowConfig(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <Gear size={16} weight="bold" />Config
              </button>
              <button data-testid="btn-regras" onClick={() => setShowRegras(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <UserCircle size={16} weight="bold" />Regras
              </button>
              {hasData && (
                <>
                  <button data-testid="btn-export-excel" onClick={() => handleExport('excel')}
                    className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium border border-[#E4E4E7] text-[#71717A] hover:text-[#1B4332] transition-all">
                    <FileXls size={14} weight="bold" />Excel
                  </button>
                  <button data-testid="btn-export-pdf" onClick={() => handleExport('pdf')}
                    className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-medium border border-[#E4E4E7] text-[#71717A] hover:text-[#DC2626] transition-all">
                    <FilePdf size={14} weight="bold" />PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Period Selector */}
        <section className="mb-5 animate-fade-in-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button data-testid="btn-prev-month" onClick={() => changeMonth(-1)}
                className="w-9 h-9 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <CaretLeft size={16} weight="bold" />
              </button>
              <div className="text-center min-w-[200px]">
                <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }} data-testid="current-period">
                  {MESES[month - 1]} {year}
                </h2>
              </div>
              <button data-testid="btn-next-month" onClick={() => changeMonth(1)}
                className="w-9 h-9 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              {!isFinalizado && (
                <button data-testid="btn-alimentar" onClick={() => setShowAlimentar(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#1B4332] transition-all">
                  <Database size={16} weight="bold" />Alimentar
                </button>
              )}
              {!isFinalizado && (
                <button data-testid="btn-gerar" onClick={handleGerar} disabled={generating}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg disabled:opacity-60"
                  style={{ backgroundColor: '#1B4332' }}>
                  {generating ? <Spinner size={16} className="animate-spin" /> : <Play size={16} weight="fill" />}
                  {generating ? 'Gerando...' : hasData ? 'Regerar' : 'Gerar Apuracao'}
                </button>
              )}
              {isRascunho && (
                <button data-testid="btn-finalizar" onClick={() => setShowFinalizar(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#1B4332] text-[#1B4332] hover:bg-[#F0FDF4] transition-all">
                  <Lock size={16} weight="bold" />Finalizar
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Finalized banner */}
        {isFinalizado && (
          <div className="mb-5 p-4 rounded-2xl border flex items-center gap-3" style={{ borderColor: '#1B433230', backgroundColor: '#F0FDF4' }} data-testid="finalizado-banner">
            <Lock size={18} weight="fill" style={{ color: '#1B4332' }} />
            <div>
              <p className="text-sm font-medium text-[#1B4332]">Fechamento finalizado</p>
              <p className="text-xs text-[#71717A]">
                {fechamento?.finalizado_por && `por ${fechamento.finalizado_por}`}
                {fechamento?.finalizado_em && ` em ${new Date(fechamento.finalizado_em).toLocaleString('pt-BR')}`}
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !hasData ? (
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <FileText size={36} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
            <h3 className="text-lg font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>Nenhuma apuracao</h3>
            <p className="text-sm text-[#71717A] mb-4">
              Primeiro, alimente a producao do periodo. Depois, gere a apuracao.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setShowAlimentar(true)} data-testid="btn-alimentar-empty"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <Database size={16} weight="bold" />Alimentar
              </button>
              <button onClick={handleGerar} disabled={generating}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg disabled:opacity-60"
                style={{ backgroundColor: '#1B4332' }}>
                <Play size={16} weight="fill" />Gerar Apuracao
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* TABELA 1 — Apuração Operacional */}
            <section className="mb-8 animate-fade-in-up" data-testid="tabela-cod">
              <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3">
                Tabela 1 — Apuracao Operacional (COD)
              </p>
              <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ backgroundColor: '#1B4332' }}>
                        {['Colaborador', 'Categoria', 'Dias Validos', 'Disponivel', 'Ausente', 'Atendimentos'].map(h => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {apuracoes.map((a, i) => (
                        <tr key={a.profissional_id} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F7F6F4]'} data-testid={`cod-row-${a.profissional_nome}`}>
                          <td className="px-4 py-3 font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{a.profissional_nome}</td>
                          <td className="px-4 py-3 text-xs text-[#71717A] capitalize">{a.categoria}</td>
                          <td className="px-4 py-3 text-center font-semibold" style={{ fontFamily: 'Outfit' }}>{a.dias_validos}</td>
                          <td className="px-4 py-3 text-center text-[#71717A]">{a.dias_disponiveis}</td>
                          <td className="px-4 py-3 text-center text-[#71717A]">{a.ausencias}</td>
                          <td className="px-4 py-3 text-center text-[#71717A]">{a.total_atendimentos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* TABELA 2 — Resumo Financeiro */}
            <section className="mb-8 animate-fade-in-up" data-testid="tabela-afo">
              <div className="flex items-center gap-3 mb-3">
                <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider">
                  Tabela 2 — Resumo Financeiro por Colaborador (AFO)
                </p>
                {fechamento?.alimentado && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wide"
                    style={{ backgroundColor: '#F0FDF4', color: '#1B4332' }} data-testid="alimentado-badge">
                    <Database size={10} weight="bold" />Alimentado
                  </span>
                )}
              </div>
              <div className="bg-white rounded-2xl border border-[#E4E4E7] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ backgroundColor: '#1B4332' }}>
                        {['Colaborador', 'Cat.', 'Fat. Serv.', 'Fat. Prod.', 'Com. Servicos', 'Com. Produtos', 'AO', 'Roy. Ind.', 'Roy. Eq.', 'Meta', 'Total'].map(h => (
                          <th key={h} className="px-3 py-3 text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap"
                            style={{ textAlign: h === 'Colaborador' || h === 'Cat.' ? 'left' : 'right' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {apuracoes.map((a, i) => (
                        <tr key={a.profissional_id} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F7F6F4]'} data-testid={`afo-row-${a.profissional_nome}`}>
                          <td className="px-3 py-3 font-medium text-[#18181B] whitespace-nowrap" style={{ fontFamily: 'Outfit' }}>{a.profissional_nome}</td>
                          <td className="px-3 py-3 text-xs text-[#71717A] capitalize">{a.categoria}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-[#71717A] text-xs">{formatCurrency(a.total_servicos)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-[#71717A] text-xs">{formatCurrency(a.total_produtos)}</td>
                          <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(a.comissao_servicos)}</td>
                          <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(a.comissao_produtos)}</td>
                          <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(a.ao)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-[#A1A1AA]">{formatCurrency(a.royalties_individual)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-[#A1A1AA]">{formatCurrency(a.royalties_equipe)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-[#A1A1AA]">{formatCurrency(a.meta_geral)}</td>
                          <td className="px-3 py-3 text-right font-bold tabular-nums" style={{ fontFamily: 'Outfit', color: '#1B4332' }}>{formatCurrency(a.total_a_receber)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
          <p className="salonup-text-xs">Gestao (CORE) &middot; AFO &middot; Agente de Fechamento Operacional</p>
        </footer>
      </div>

      {/* Modals */}
      {showConfig && <ConfigModal onClose={() => setShowConfig(false)} onSaved={() => { setShowConfig(false); }} />}
      {showRegras && <RegrasModal profissionais={apuracoes.length > 0 ? apuracoes : []} onClose={() => setShowRegras(false)} />}
      {showFinalizar && <FinalizarModal periodo={periodo} onClose={() => setShowFinalizar(false)} onSuccess={() => { setShowFinalizar(false); fetchFechamento(); }} />}
      {showAlimentar && <AlimentarModal periodo={periodo} onClose={() => setShowAlimentar(false)} onSaved={() => { setShowAlimentar(false); }} />}
    </div>
  );
};

export default AfoPage;
